'use client'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { cn } from '@/lib/utils'
import { ArrowUp } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useState } from 'react'

interface Feature {
  icon: string
  title: string
  subtitle?: string
  description?: string
}

interface Props {
  features: Feature[]
  rawText?: boolean
}

export const CategoryFeatureMobile = ({ features, rawText = false }: Props) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const t = useTranslations()
  const getText = (text: string) => (rawText ? text : t(text))

  return (
    <Carousel
      opts={{ align: 'start', loop: true, skipSnaps: false, slidesToScroll: 1 }}
      className='w-full'
      setApi={(api) => {
        if (!api) return
        setSelectedIndex(api.selectedScrollSnap())
        api.on('select', () => setSelectedIndex(api.selectedScrollSnap()))
      }}
    >
      <CarouselContent className='-ml-4'>
        {features.map((feature, index) => (
          <CarouselItem
            key={index}
            className={cn(
              'cursor-pointer pl-4',
              'basis-[83.333%]',
              'border-r border-gray-600',
              index !== selectedIndex && 'opacity-40'
            )}
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
          >
            <div className='relative h-full px-6 py-8 transition-all duration-500 ease-in-out'>
              {/* Default state (visible when not hovered) */}
              <div
                className={cn(
                  'flex h-full flex-col justify-between opacity-100 transition-all duration-500 ease-in-out',
                  openIndex === index && 'opacity-0'
                )}
              >
                <div className='flex w-full items-center justify-center'>
                  <Image
                    src={ImageHelper.getImageUrlCdn(feature.icon)}
                    alt={feature.title}
                    width={180}
                    height={180}
                    className='h-28 w-28 2xl:h-44 2xl:w-44'
                  />
                </div>
                <div className='mt-6 flex w-full items-center justify-between'>
                  <div className='text-white'>
                    <h3 className='mb-1 text-xl font-medium md:text-2xl'>
                      {getText(feature.title)}
                    </h3>
                    {feature.subtitle ? (
                      <p className='text-sm text-gray-300 md:text-lg'>
                        {getText(feature.subtitle)}
                      </p>
                    ) : null}
                  </div>
                  <button className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-black'>
                    <ArrowUp className='h-4 w-4' />
                  </button>
                </div>
              </div>
              {/* Hover state (visible when hovered) with slide-up animation */}
              <div
                className={cn(
                  'absolute inset-0 translate-y-4 px-4 opacity-0 transition-all duration-500 ease-in-out',
                  openIndex === index && 'translate-y-0 opacity-100'
                )}
              >
                <div>
                  <h3 className='mb-2 text-2xl font-semibold text-white'>
                    {getText(feature.title)}
                  </h3>
                  {feature.subtitle ? (
                    <p className='text-sm text-gray-300 md:text-lg'>
                      {getText(feature.subtitle)}
                    </p>
                  ) : null}
                  <p className='mt-2 border-t pt-2 text-sm leading-relaxed text-gray-300 md:text-lg'>
                    {feature?.description ? getText(feature?.description) : ''}
                  </p>
                </div>
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}
